import { type FormEvent, useState } from "react";
import { searchCatalog } from "../../api/client";
import type { CatalogSource, Images, ProductRef } from "../../api/models";
import { CatalogThumb } from "../../components/CatalogThumb";
import { PriceLine } from "../../components/PriceLine";
import { errorMessage } from "../../lib/format";

export function SearchBox(props: {
  initialSource: CatalogSource;
  /** The engine's own query per source; prefilled until the reviewer types. */
  initialQuery: Record<CatalogSource, string>;
  onPick: (product: ProductRef, source: CatalogSource, images: Images) => void;
}) {
  const [source, setSourceState] = useState<CatalogSource>(props.initialSource);
  const [q, setQ] = useState(props.initialQuery[props.initialSource]);
  const [edited, setEdited] = useState(false);
  const setSource = (next: CatalogSource) => {
    setSourceState(next);
    if (!edited) setQ(props.initialQuery[next]);
  };
  const [results, setResults] = useState<{ products: ProductRef[]; images: Images } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      setResults(await searchCatalog(q.trim(), source));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel">
      <form className="toolbar" onSubmit={submit} style={{ marginBottom: results ? 8 : 0 }}>
        <input
          type="search"
          placeholder="Search the catalog (Enter)"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setEdited(true);
          }}
          style={{ flex: 1, minWidth: 200 }}
        />
        <label className="field">
          <input type="radio" name="source" checked={source === "scp"} onChange={() => setSource("scp")} />
          SportsCardsPro
        </label>
        <label className="field">
          <input
            type="radio"
            name="source"
            checked={source === "pricecharting"}
            onChange={() => setSource("pricecharting")}
          />
          PriceCharting
        </label>
        <button type="submit" disabled={busy}>
          Search
        </button>
        {error && <span className="error">{error}</span>}
      </form>
      {results && (
        <div className="search-results">
          {results.products.length === 0 && <span className="muted">No products.</span>}
          {results.products.map((p) => (
            <button
              type="button"
              key={p.id}
              className="search-result"
              onClick={() => props.onPick(p, source, results.images)}
            >
              <CatalogThumb url={results.images[p.id]} small />
              <span>
                <span className="name">{p.name}</span>
                <br />
                <span className="muted">
                  {p.console} · <span className="mono">#{p.id}</span>
                </span>
                {p.prices && (
                  <>
                    <br />
                    <PriceLine prices={p.prices} />
                  </>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
